import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchRuleTemplate } from '@/api/risk';
import type { RuleTemplate } from '@/api/risk/types';

export const FORMULA_METRICS = [
  { id: 'SEVERITY', value: 'Severity' },
  { id: 'LIKELIHOOD', value: 'Likelihood' },
  { id: 'COMPANY_LEVERAGE', value: 'Company leverage' },
];

/** Old frontend stored fields as { key, text } objects. Normalize to string. */
function extractKey(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return String(o.key ?? o.id ?? o.text ?? '');
  }
  return '';
}

/** API stores formula as plain expression: "(SEVERITY+LIKELIHOOD)*COMPANY_LEVERAGE".
 *  Keep it as-is — FormulaEditor handles both JSON token format and plain string. */
function normalizeFormula(formula: string | null): string {
  if (!formula) return '';
  // Already JSON token format
  if (formula.trim().startsWith('[')) return formula;
  // Plain expression — return as-is; FormulaEditor will show it as read-only text
  return formula;
}

function normalizeTemplate(raw: RuleTemplate): RuleTemplate {
  return {
    ...raw,
    scoreFormula: normalizeFormula(raw.scoreFormula),
    tags: Array.isArray(raw.tags)
      ? raw.tags.map(t => (typeof t === 'string' ? t : extractKey(t)))
      : [],
    rules: (raw.rules ?? []).map(rule => ({
      ...rule,
      triggers: (rule.triggers ?? []).map(t => ({
        ...t,
        dataPoint: extractKey(t.dataPoint),
        connectorType: extractKey(t.connectorType),
        asset: t.asset ? extractKey(t.asset) : undefined,
        assetType: t.assetType
          ? typeof t.assetType === 'object' && !Array.isArray(t.assetType)
            ? extractKey(t.assetType)
            : Array.isArray(t.assetType)
              ? (t.assetType as unknown[]).map(extractKey).join(',')
              : String(t.assetType)
          : '',
        operator: (extractKey(t.operator) || 'and') as 'and' | 'or',
      })),
      offsets: (rule.offsets ?? []).map(o => ({
        ...o,
        offsetType: extractKey(o.offsetType),
        metrics: extractKey(o.metrics),
        riskFactors: Array.isArray(o.riskFactors)
          ? o.riskFactors.map(rf => extractKey(rf))
          : [],
        operator: (extractKey((o as { operator?: unknown }).operator) ||
          'and') as 'and' | 'or',
      })),
    })),
  };
}

/** Shared with the route loaders that prefetch the editor's template. */
export function ruleTemplateQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['risk', 'rule-template', id] as const,
    queryFn: () => fetchRuleTemplate(id),
    staleTime: 60_000,
  });
}

export function useRuleTemplate(id: string) {
  const template = useQuery({
    ...ruleTemplateQueryOptions(id),
    // Normalising in `select` keeps the cache entry raw, so the loader's
    // prefetch and this read are the same entry.
    select: data => normalizeTemplate(data),
  });

  return { template, metrics: FORMULA_METRICS };
}
